const resources = {
  "en": {
    "recurrentExpenseManager": {
      "title": "Recurring expenses",
      "description": "Keep recurring obligations readable, predictable, and easy to maintain without changing how they execute.",
      "headerMeta": "Recurring expense schedules",
      "registryTitle": "Expense registry",
      "registryDescription": "Review active setups, confirm source accounts, and open the existing create or edit flows.",
      "metrics": {
        "totalSetups": "Expense setups",
        "totalSetupsMeta": "{{count}} vendors linked",
        "essentialSetups": "Essential setups",
        "linkedAccountsMeta": "{{count}} source accounts linked",
        "plannedTotal": "Planned total",
        "plannedTotalMeta": "6-month runway: {{amount}}"
      },
      "accessibility": {
        "actions": "Recurring expense actions"
      },
      "actions": {
        "add": "Add",
        "edit": "Edit",
        "pause": "Pause",
        "resume": "Resume",
        "delete": "Delete",
        "title": "Tools",
        "description": "Use the existing forecast preview without changing the recurring setup list.",
        "viewForecast": "View forecast",
        "openMenu": "Open expense actions",
        "closeMenu": "Close expense actions"
      },
      "status": {
        "active": "Active",
        "inactive": "Inactive",
        "paused": "Paused"
      },
      "list": {
        "base": "💸 Base: {{amount}}",
        "tax": "🧾 Tax: {{amount}}",
        "total": "💰 Total: {{amount}}",
        "taxIncluded": "🧾 Tax included",
        "frequency": "🔁 {{frequency}} starting {{date}}",
        "type": "📂 Type: {{type}}",
        "billingDay": "📅 Billing Day: {{day}}",
        "weekday": "📅 Weekday: {{weekday}}",
        "vendor": "🏢 Vendor: {{vendor}}",
        "essential": "🌟 Essential",
        "source": "🏦 Source: {{institution}} ({{type}})",
        "unknownSource": "—",
        "accountWithCurrency": "🏦 Source: {{account}} • {{currency}}",
        "accountMissingCurrency": "🏦 Source: {{account}} • currency missing — reassign to a valid account",
        "deletedAccountWarning": "🏦 {{account}} was deleted — reassign to an active account",
        "missingAccountWarning": "🏦 Source account missing — reassign to a valid account",
        "status": "Status: {{status}}",
        "empty": "No recurring expenses yet",
        "loading": "Loading recurring expenses…",
        "error": "Unable to display recurring expenses right now."
      },
      "summary": {
        "title": "Summary",
        "description": "Track the current recurring load across currencies while respecting amount visibility settings.",
        "total": "Total: {{amount}}",
        "essentialTotal": "Essential Total: {{amount}}",
        "totalByCurrency": "Total ({{currency}}): {{amount}}",
        "essentialTotalByCurrency": "Essential Total ({{currency}}): {{amount}}",
        "items": "Items: {{count}}",
        "vendors": "Vendors: {{count}}",
        "runway": "Runway (6m): {{amount}}"
      },
      "form": {
        "editTitle": "Edit Recurrent Expense",
        "addTitle": "Add Recurrent Expense",
        "save": "Save",
        "description": "Description",
        "descriptionPlaceholder": "e.g. Spotify, Health Insurance",
        "amount": "Amount",
        "amountPlaceholder": "Enter amount",
        "amountHint": "Decimals are supported. Use a dot, for example: 12.34",
        "category": "Business category",
        "categoryPlaceholder": "Select what this expense means",
        "startDate": "Start Date",
        "startDatePlaceholder": "Select start date",
        "frequency": "Frequency",
        "frequencyPlaceholder": "Select frequency",
        "source": "Source Account",
        "sourcePlaceholder": "Select account",
        "accountLabel": "{{institution}} — {{type}}",
        "accountRequired": "Please select an account",
        "accountInvalid": "Please select a valid account",
        "accountGoalNotAllowed": "Goal vault account cannot be used for recurring expenses. Please select another account.",
        "accountDeleted": "Selected account is deleted. Please choose another account.",
        "accountCurrencyRequired": "Selected account must include a currency",
        "accountMissingTitle": "Select a valid account",
        "accountMissingMessage": "The linked account no longer exists. Please pick another account to continue.",
        "accountCurrencyMissingTitle": "Account needs a currency",
        "accountCurrencyMissingMessage": "The selected account is missing a currency. Reassign this recurrence to an account with currency.",
        "accountDeletedBadge": "Deleted",
        "currencyMissingBadge": "Missing currency",
        "validAccountsGroup": "Ready to use",
        "attentionAccountsGroup": "Needs attention",
        "attentionHelper": "{{count}} account(s) need a currency before they can be selected.",
        "manageAccountsCta": "Fix in account manager",
        "accountDeletedWarning": "This account was deleted. Please reassign to a valid account.",
        "accountMeta": "Currency: {{currency}} • Asset type: {{assetType}}",
        "accountCurrencyOnly": "Currency: {{currency}}",
        "currencyMissingShort": "Currency missing",
        "type": "Type",
        "typePlaceholder": "Select type",
        "vendor": "Vendor",
        "vendorPlaceholder": "e.g. Netflix, IRS",
        "essential": "Essential",
        "essentialHint": "Mark as essential expense",
        "taxIncluded": "Tax Included",
        "taxAmount": "Tax Amount",
        "taxAmountPlaceholder": "Enter tax amount",
        "taxAmountHint": "Decimals are supported. Use a dot, for example: 1.23",
        "taxIncludedHint": "Toggle if tax is part of the amount",
        "billingDay": "Billing Day",
        "billingDayPlaceholder": "Select billing day",
        "billingDayOption": "Day {{day}} of month",
        "weekday": "Weekday",
        "weekdayPlaceholder": "Select weekday",
        "scheduleHintDaily": "The system creates a task every day shortly after midnight in {{timezone}}.",
        "scheduleHintWeeklyDefault": "The system creates a task every week on the same weekday as the start date: {{weekday}}. It will run shortly after midnight in {{timezone}}.",
        "scheduleHintWeeklyCustom": "The system creates a task every week on {{weekday}}. It will run shortly after midnight in {{timezone}}.",
        "scheduleHintBiWeeklyDefault": "The system creates a task every 2 weeks on the same weekday as the start date: {{weekday}}. It will run shortly after midnight in {{timezone}}.",
        "scheduleHintBiWeeklyCustom": "The system creates a task every 2 weeks on {{weekday}}. The first run will be on the first matching weekday on or after the start date, shortly after midnight in {{timezone}}.",
        "scheduleHintMonthlyDefault": "The system creates a task every month on the same day as the start date: {{day}}. It will run shortly after midnight in {{timezone}}.",
        "scheduleHintMonthlyCustom": "The system creates a task every month on day {{day}}. It will run shortly after midnight in {{timezone}}.",
        "scheduleHintYearly": "The system creates a task every year on {{date}}, shortly after midnight in {{timezone}}.",
        "scheduleHintStartDateNeeded": "Select a start date to preview how the schedule will run.",
        "cancel": "Cancel",
        "submit": "Submit"
      },
      "frequency": {
        "daily": "Daily",
        "weekly": "Weekly",
        "biWeekly": "Bi-weekly",
        "monthly": "Monthly",
        "yearly": "Yearly"
      },
      "weekdays": {
        "sunday": "Sunday",
        "monday": "Monday",
        "tuesday": "Tuesday",
        "wednesday": "Wednesday",
        "thursday": "Thursday",
        "friday": "Friday",
        "saturday": "Saturday"
      },
      "types": {
        "subscription": "Subscription",
        "tax": "Tax",
        "utility": "Utility",
        "rent": "Rent",
        "other": "Other"
      },
      "popconfirm": {
        "title": "Delete this recurring expense?",
        "description": "This action cannot be undone.",
        "okText": "Delete",
        "cancelText": "Cancel",
        "genericTitle": "Are you sure?",
        "deactivate": "This will pause this recurring expense until reactivated.",
        "activate": "This will resume the recurring expense schedule."
      },
      "messages": {
        "errorWithReason": "❌ {{error}}",
        "loadExpensesError": "❌ Failed to load recurrent expenses",
        "loadAccountsError": "❌ Failed to load accounts",
        "addSuccess": "✅ Expense added",
        "updateSuccess": "✅ Expense updated",
        "deleteSuccess": "✅ Expense deleted",
        "pauseSuccess": "Recurring expense paused",
        "resumeSuccess": "Recurring expense resumed",
        "activateSuccess": "✅ Expense activated",
        "deactivateSuccess": "✅ Expense deactivated",
        "submitError": "❌ Unexpected error",
        "taxAmountInvalid": "❌ Tax amount is invalid. Leave it empty or enter a valid number.",
        "deleteError": "❌ Delete failed",
        "lifecycleError": "Unable to update recurring expense status",
        "activateError": "❌ Failed to activate expense",
        "deactivateError": "❌ Failed to deactivate expense",
        "accountCurrencyRequired": "Selected account must include a currency"
      }
    }
  },
  "uk": {
    "recurrentExpenseManager": {
      "title": "Регулярні витрати",
      "description": "Керуйте регулярними зобов’язаннями спокійно й прозоро, не змінюючи їхню логіку виконання.",
      "headerMeta": "Розклади регулярних витрат",
      "registryTitle": "Реєстр витрат",
      "registryDescription": "Переглядайте активні налаштування, перевіряйте рахунки-джерела та відкривайте наявні create/edit флоу.",
      "metrics": {
        "totalSetups": "Налаштування витрат",
        "totalSetupsMeta": "{{count}} постачальників пов’язано",
        "essentialSetups": "Необхідні налаштування",
        "linkedAccountsMeta": "{{count}} рахунків-джерел пов’язано",
        "plannedTotal": "Запланована сума",
        "plannedTotalMeta": "Запас на 6 міс: {{amount}}"
      },
      "accessibility": {
        "actions": "Дії з регулярними витратами"
      },
      "actions": {
        "add": "Додати",
        "edit": "Редагувати",
        "pause": "Призупинити",
        "resume": "Відновити",
        "delete": "Видалити",
        "title": "Інструменти",
        "description": "Відкривайте наявний прогноз без змін у реєстрі регулярних витрат.",
        "viewForecast": "Переглянути прогноз",
        "openMenu": "Відкрити дії з витратою",
        "closeMenu": "Закрити дії з витратою"
      },
      "status": {
        "active": "Активна",
        "inactive": "Неактивна",
        "paused": "Призупинена"
      },
      "list": {
        "base": "💸 База: {{amount}}",
        "tax": "🧾 Податок: {{amount}}",
        "total": "💰 Разом: {{amount}}",
        "taxIncluded": "🧾 Включно з податком",
        "frequency": "🔁 {{frequency}} з {{date}}",
        "type": "📂 Тип: {{type}}",
        "billingDay": "📅 День білінгу: {{day}}",
        "weekday": "📅 День тижня: {{weekday}}",
        "vendor": "🏢 Постачальник: {{vendor}}",
        "essential": "🌟 Необхідна",
        "source": "🏦 Джерело: {{institution}} ({{type}})",
        "unknownSource": "—",
        "accountWithCurrency": "🏦 Джерело: {{account}} • {{currency}}",
        "accountMissingCurrency": "🏦 Джерело: {{account}} • відсутня валюта — призначте дійсний рахунок",
        "deletedAccountWarning": "🏦 {{account}} було видалено — перепризначте на активний рахунок",
        "missingAccountWarning": "🏦 Рахунок джерела відсутній — перепризначте на дійсний рахунок",
        "status": "Статус: {{status}}",
        "empty": "Регулярних витрат поки немає",
        "loading": "Завантаження регулярних витрат…",
        "error": "Зараз не вдається показати регулярні витрати."
      },
      "summary": {
        "title": "Підсумок",
        "description": "Відстежуйте поточне регулярне навантаження в різних валютах з урахуванням режиму приховування сум.",
        "total": "Разом: {{amount}}",
        "essentialTotal": "Необхідні витрати: {{amount}}",
        "totalByCurrency": "Разом ({{currency}}): {{amount}}",
        "essentialTotalByCurrency": "Необхідні витрати ({{currency}}): {{amount}}",
        "items": "Записи: {{count}}",
        "vendors": "Постачальники: {{count}}",
        "runway": "Запас (6 міс): {{amount}}"
      },
      "form": {
        "editTitle": "Редагувати регулярну витрату",
        "addTitle": "Додати регулярну витрату",
        "save": "Зберегти",
        "description": "Опис",
        "descriptionPlaceholder": "наприклад, Spotify, Страхування здоров’я",
        "amount": "Сума",
        "amountPlaceholder": "Введіть суму",
        "amountHint": "Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34",
        "category": "Бізнес-категорія",
        "categoryPlaceholder": "Оберіть, що означає ця витрата",
        "startDate": "Дата початку",
        "startDatePlaceholder": "Оберіть дату початку",
        "frequency": "Частота",
        "frequencyPlaceholder": "Виберіть частоту",
        "source": "Рахунок джерела",
        "sourcePlaceholder": "Виберіть рахунок",
        "accountLabel": "{{institution}} — {{type}}",
        "accountRequired": "Будь ласка, виберіть рахунок",
        "accountInvalid": "Будь ласка, виберіть дійсний рахунок",
        "accountGoalNotAllowed": "Рахунок Goal Vault не можна використовувати для регулярних витрат. Будь ласка, виберіть інший рахунок.",
        "accountDeleted": "Вибраний рахунок видалено. Будь ласка, оберіть інший.",
        "accountCurrencyRequired": "Обраний рахунок повинен мати валюту",
        "accountMissingTitle": "Оберіть дійсний рахунок",
        "accountMissingMessage": "Пов’язаний рахунок більше не існує. Будь ласка, виберіть інший рахунок.",
        "accountCurrencyMissingTitle": "Рахунку потрібна валюта",
        "accountCurrencyMissingMessage": "У вибраному рахунку відсутня валюта. Перепризначте цю регулярну витрату на рахунок із валютою.",
        "accountDeletedBadge": "Видалено",
        "currencyMissingBadge": "Немає валюти",
        "validAccountsGroup": "Готові до використання",
        "attentionAccountsGroup": "Потребують уваги",
        "attentionHelper": "{{count}} рахунок(-и) потребують валюти, перш ніж їх можна буде вибрати.",
        "manageAccountsCta": "Відкрити менеджер рахунків",
        "accountDeletedWarning": "Цей рахунок видалено. Будь ласка, призначте дійсний рахунок.",
        "accountMeta": "Валюта: {{currency}} • Тип активу: {{assetType}}",
        "accountCurrencyOnly": "Валюта: {{currency}}",
        "currencyMissingShort": "Валюта відсутня",
        "type": "Тип",
        "typePlaceholder": "Виберіть тип",
        "vendor": "Постачальник",
        "vendorPlaceholder": "наприклад, Netflix, IRS",
        "essential": "Необхідна",
        "essentialHint": "Позначити як необхідну витрату",
        "taxIncluded": "Включно з податком",
        "taxAmount": "Сума податку",
        "taxAmountPlaceholder": "Введіть суму податку",
        "taxAmountHint": "Дробова частина підтримується. Використовуйте крапку, наприклад: 1.23",
        "taxIncludedHint": "Увімкніть, якщо податок входить до суми",
        "billingDay": "День білінгу",
        "billingDayPlaceholder": "Виберіть день білінгу",
        "billingDayOption": "День {{day}} місяця",
        "weekday": "День тижня",
        "weekdayPlaceholder": "Виберіть день тижня",
        "scheduleHintDaily": "Система створює задачу щодня невдовзі після опівночі у вашому часовому поясі {{timezone}}.",
        "scheduleHintWeeklyDefault": "Система створює задачу щотижня в той самий день тижня, що й дата старту: {{weekday}}. Запуск буде невдовзі після опівночі у {{timezone}}.",
        "scheduleHintWeeklyCustom": "Система створює задачу щотижня у {{weekday}}. Запуск буде невдовзі після опівночі у {{timezone}}.",
        "scheduleHintBiWeeklyDefault": "Система створює задачу раз на 2 тижні в той самий день тижня, що й дата старту: {{weekday}}. Запуск буде невдовзі після опівночі у {{timezone}}.",
        "scheduleHintBiWeeklyCustom": "Система створює задачу раз на 2 тижні у {{weekday}}. Перший запуск буде у перший відповідний день на або після дати старту, невдовзі після опівночі у {{timezone}}.",
        "scheduleHintMonthlyDefault": "Система створює задачу щомісяця в той самий день місяця, що й дата старту: {{day}}. Запуск буде невдовзі після опівночі у {{timezone}}.",
        "scheduleHintMonthlyCustom": "Система створює задачу щомісяця у день {{day}}. Запуск буде невдовзі після опівночі у {{timezone}}.",
        "scheduleHintYearly": "Система створює задачу щороку {{date}}, невдовзі після опівночі у {{timezone}}.",
        "scheduleHintStartDateNeeded": "Оберіть дату старту, щоб побачити, як працюватиме розклад.",
        "cancel": "Скасувати",
        "submit": "Надіслати"
      },
      "frequency": {
        "daily": "Щодня",
        "weekly": "Щотижня",
        "biWeekly": "Раз на два тижні",
        "monthly": "Щомісяця",
        "yearly": "Щороку"
      },
      "weekdays": {
        "sunday": "Неділя",
        "monday": "Понеділок",
        "tuesday": "Вівторок",
        "wednesday": "Середа",
        "thursday": "Четвер",
        "friday": "П’ятниця",
        "saturday": "Субота"
      },
      "types": {
        "subscription": "Підписка",
        "tax": "Податок",
        "utility": "Комунальні послуги",
        "rent": "Оренда",
        "other": "Інше"
      },
      "popconfirm": {
        "title": "Видалити цю регулярну витрату?",
        "description": "Цю дію неможливо скасувати.",
        "okText": "Видалити",
        "cancelText": "Скасувати",
        "genericTitle": "Ви впевнені?",
        "deactivate": "Це призупинить регулярну витрату до повторної активації.",
        "activate": "Це відновить графік регулярної витрати."
      },
      "messages": {
        "errorWithReason": "❌ {{error}}",
        "loadExpensesError": "❌ Не вдалося завантажити регулярні витрати",
        "loadAccountsError": "❌ Не вдалося завантажити рахунки",
        "addSuccess": "✅ Витрату додано",
        "updateSuccess": "✅ Витрату оновлено",
        "deleteSuccess": "✅ Витрату видалено",
        "pauseSuccess": "Регулярну витрату призупинено",
        "resumeSuccess": "Регулярну витрату відновлено",
        "activateSuccess": "✅ Витрату активовано",
        "deactivateSuccess": "✅ Витрату деактивовано",
        "submitError": "❌ Неочікувана помилка",
        "taxAmountInvalid": "❌ Сума податку вказана некоректно. Залиште поле порожнім або введіть коректне число.",
        "deleteError": "❌ Помилка видалення",
        "lifecycleError": "Не вдалося оновити статус регулярної витрати",
        "activateError": "❌ Не вдалося активувати витрату",
        "deactivateError": "❌ Не вдалося деактивувати витрату",
        "accountCurrencyRequired": "Обраний рахунок повинен мати валюту"
      }
    }
  }
};

export default resources;
